import { z } from 'zod';
import {
  energyUnlockItemId,
  finishUnlockItemId,
  PET_CHASSIS_FINISHES,
  PET_ENERGY_COLORS,
} from './pet-appearance';

/**
 * Everything a player can consume ON a pet.
 *
 * Three families, and they behave differently on purpose:
 *
 * - **Foods** are PERMANENT TRAINING on one instance. Eating one raises a
 *   counter on that pet forever; the pet carries it through evolution and,
 *   when trading exists, to its next owner.
 * - **Energy Spheres** UNLOCK a colour for the ACCOUNT, once. A second copy of
 *   a colour already unlocked is refused without being spent.
 * - **Tech Paint Buckets** unlock a chassis finish, the same way.
 *
 * All three are stackable consumables: quantity lives in `item_stacks`, not in
 * individually-identified rows, because one food is indistinguishable from
 * another.
 */

/* -------------------------------------------------------------------------- */
/*  Foods                                                                     */
/* -------------------------------------------------------------------------- */

export const PET_FOOD_DAMAGE_ID = 'pet_food_damage';
export const PET_FOOD_ATTACK_SPEED_ID = 'pet_food_attack_speed';

export const PET_FOOD_IDS = [PET_FOOD_DAMAGE_ID, PET_FOOD_ATTACK_SPEED_ID] as const;
export type PetFoodId = (typeof PET_FOOD_IDS)[number];
export const petFoodIdSchema = z.enum(PET_FOOD_IDS);

/**
 * Provisional training values.
 *
 * Both are ADDITIVE percentages of the pet's own base, never of the ship's:
 * a fed pet must not make the ship stronger, and the two must never be
 * multiplied together into a number nobody can predict.
 *
 * Centralised here and nowhere else, and provisional until balance says
 * otherwise - see `docs/OPEN_QUESTIONS.md`.
 */
export const PET_FOOD_EFFECTS = Object.freeze({
  /** Basis points of pet damage per unit. 100 bps = +1%. */
  damageBpsPerUnit: 100,
  /** Basis points of pet base attack speed per unit. 50 bps = +0.5%. */
  attackSpeedBpsPerUnit: 50,
} as const);

export interface PetFoodDefinition {
  readonly id: PetFoodId;
  readonly name: string;
  readonly description: string;
  /** Which counter on the instance it raises. */
  readonly counter: 'damage' | 'attackSpeed';
  readonly bpsPerUnit: number;
  readonly assetKey: string;
}

export const PET_FOODS: readonly PetFoodDefinition[] = Object.freeze([
  {
    id: PET_FOOD_DAMAGE_ID,
    name: 'Ração de Combate',
    description: 'Treinamento permanente. Cada unidade concede +1% de dano ao pet alimentado.',
    counter: 'damage',
    bpsPerUnit: PET_FOOD_EFFECTS.damageBpsPerUnit,
    assetKey: 'pet_food_damage',
  },
  {
    id: PET_FOOD_ATTACK_SPEED_ID,
    name: 'Estimulante Sináptico',
    description:
      'Treinamento permanente. Cada unidade concede +0,5% da velocidade de ataque base do pet.',
    counter: 'attackSpeed',
    bpsPerUnit: PET_FOOD_EFFECTS.attackSpeedBpsPerUnit,
    assetKey: 'pet_food_attack_speed',
  },
]);

const FOOD_BY_ID = new Map(PET_FOODS.map((food) => [food.id, food]));

export function findPetFood(id: string): PetFoodDefinition | null {
  return FOOD_BY_ID.get(id as PetFoodId) ?? null;
}

/* -------------------------------------------------------------------------- */
/*  Unlock consumables                                                        */
/* -------------------------------------------------------------------------- */

export interface PetUnlockConsumable {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly kind: 'energy' | 'finish';
  /** The energy or finish id this unlocks. */
  readonly unlocksId: string;
  readonly dropWeight: number;
  readonly assetKey: string;
}

/**
 * **Uma arte por COR, e não uma partilhada pelas dez** (gate V28I.BL).
 *
 * Até este gate as dez esferas apontavam para `pet_energy_sphere`, e os quinze
 * baldes para `pet_paint_bucket`. Nenhuma das duas chaves tinha ficheiro, o que
 * escondia o problema: assim que a arte existisse, um Roxo do Vazio e um Verde
 * Tóxico desenhar-se-iam com a MESMA imagem — e um jogador com as duas no
 * inventário não teria como as distinguir sem ler o nome.
 *
 * A chave passou a ser o próprio id da definição, como já acontece nas naves e
 * nos artefactos. As imagens são geradas a partir desta mesma tabela por
 * `pnpm assets:consumables`, portanto uma cor nova ganha arte sem ninguém editar
 * uma segunda lista.
 */
export const PET_ENERGY_SPHERES: readonly PetUnlockConsumable[] = Object.freeze(
  PET_ENERGY_COLORS.map((energy) => ({
    id: energyUnlockItemId(energy.id),
    name: `Esfera de Energia: ${energy.name}`,
    description: `Desbloqueia permanentemente a cor de efeitos ${energy.name} para a conta.`,
    kind: 'energy' as const,
    unlocksId: energy.id,
    dropWeight: energy.dropWeight,
    assetKey: energyUnlockItemId(energy.id),
  })),
);

export const PET_PAINT_BUCKETS: readonly PetUnlockConsumable[] = Object.freeze(
  PET_CHASSIS_FINISHES.map((finish) => ({
    id: finishUnlockItemId(finish.id),
    name: `Balde de Tinta: ${finish.name}`,
    description: `Desbloqueia permanentemente o acabamento de lataria ${finish.name} para a conta.`,
    kind: 'finish' as const,
    unlocksId: finish.id,
    dropWeight: finish.dropWeight,
    /* Uma arte por ACABAMENTO. Ver a nota das esferas, logo acima. */
    assetKey: finishUnlockItemId(finish.id),
  })),
);

export const PET_UNLOCK_CONSUMABLES: readonly PetUnlockConsumable[] = Object.freeze([
  ...PET_ENERGY_SPHERES,
  ...PET_PAINT_BUCKETS,
]);

const UNLOCK_BY_ID = new Map(PET_UNLOCK_CONSUMABLES.map((entry) => [entry.id, entry]));

export function findPetUnlockConsumable(id: string): PetUnlockConsumable | null {
  return UNLOCK_BY_ID.get(id) ?? null;
}

/** Every consumable this system introduces, in catalog order. */
export const PET_CONSUMABLE_IDS: readonly string[] = Object.freeze([
  ...PET_FOOD_IDS,
  ...PET_UNLOCK_CONSUMABLES.map((entry) => entry.id),
]);

export function isPetConsumableId(id: string): boolean {
  return findPetFood(id) !== null || findPetUnlockConsumable(id) !== null;
}

/* -------------------------------------------------------------------------- */
/*  Training maths                                                            */
/* -------------------------------------------------------------------------- */

/**
 * A SAFE CEILING on how many projectiles a pet may put in the air per second.
 *
 * Training is uncapped by design - a player who feeds a thousand foods has
 * earned what they bought - but the RENDERER is not infinite, and neither is
 * the tick. This is a technical limit, documented and centralised, not a
 * balance decision: it exists so a pathological account cannot allocate
 * projectiles without bound inside one 16 ms tick.
 *
 * It is deliberately far above anything reachable by ordinary play.
 */
export const PET_MAX_PROJECTILES_PER_SECOND = 240;

/** Total damage bonus in basis points, from foods alone. */
export function petDamageBonusBps(damageFoodCount: number): number {
  return Math.max(0, Math.trunc(damageFoodCount)) * PET_FOOD_EFFECTS.damageBpsPerUnit;
}

/** Total attack-speed bonus in basis points, from foods alone. */
export function petAttackSpeedBonusBps(attackSpeedFoodCount: number): number {
  return Math.max(0, Math.trunc(attackSpeedFoodCount)) * PET_FOOD_EFFECTS.attackSpeedBpsPerUnit;
}

/** The training state of one pet instance, as the wire carries it. */
export const petTrainingSchema = z.object({
  damageFoodCount: z.number().int().min(0),
  attackSpeedFoodCount: z.number().int().min(0),
  damageBonusBps: z.number().int().min(0),
  attackSpeedBonusBps: z.number().int().min(0),
});
export type PetTrainingWire = z.infer<typeof petTrainingSchema>;

/** Derives the wire shape from the two stored counters. */
export function petTrainingOf(
  damageFoodCount: number,
  attackSpeedFoodCount: number,
): PetTrainingWire {
  return {
    damageFoodCount: Math.max(0, Math.trunc(damageFoodCount)),
    attackSpeedFoodCount: Math.max(0, Math.trunc(attackSpeedFoodCount)),
    damageBonusBps: petDamageBonusBps(damageFoodCount),
    attackSpeedBonusBps: petAttackSpeedBonusBps(attackSpeedFoodCount),
  };
}
